import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { useTradePairInfos } from '@ui/hooks/trade/useTradePairInfos';
import { useGasFee } from '@ui/hooks/useGasFee';
import { usePayTokenBalance } from '@ui/hooks/usePayTokenBalance';
import { ZERO_BIG_NUMBER } from '@ui/lib/bignumber';
import { useEstimateAddMarginGasLimit } from '@ui/services/contract/hooks/TradingPortalFacet/useEstimateAddMarginGasLimit';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { getBalanceAfterGas } from '@ui/utils/getBalanceAfterGas';
import { getLeverage } from '@ui/utils/trade/getLeverage';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { create } from 'zustand';

import { Value } from '../../../types';
import { useMaxInput } from '../../TradeModule/hooks/useMaxInput';
import { validateDepositCollateralAmount } from '../../TradeModule/utils/validateDepositCollateralAmount';
import { useMinLeverage } from './useMinLeverage';

type InputState = {
  input: {
    value: Value;
  };
  setInput: (input: InputState['input']) => void;
  reset: () => void;
  useValidation: (param: {
    position: Position;
  }) => ReturnType<typeof validateDepositCollateralAmount>;
};

export const useDepositCollateralInput = create<InputState>((set, get) => ({
  input: {
    value: {
      usd: null,
      token: null,
    },
  },
  reset: () => set({ input: { value: { usd: null, token: null } } }),
  setInput: (input) => set({ input }),
  useValidation: ({ position }) => {
    const input = get().input;
    const payTokenBalance = usePayTokenBalance(position.collateralToken);
    const { data: gasLimit } = useEstimateAddMarginGasLimit({
      amount: input.value.token,
      payToken: position.collateralToken,
      positionHash: position.id,
      opts: {
        enabled: !isNil(input.value.token),
      },
    });
    const { data: gas } = useGasFee(gasLimit);
    const maxValue = useMemo(() => {
      return getBalanceAfterGas({
        balance: payTokenBalance,
        gasFee: gas?.gasInNativeToken,
        isNative: position.collateralToken.isNative,
      });
    }, [gas, payTokenBalance, position.collateralToken.isNative]);

    const payTokenPrice =
      usePayTokenPriceLatest(position.collateralToken) ?? ZERO_BIG_NUMBER;

    const currentLeverage = position.getLeverage({
      collateralTokenMarketPrice: payTokenPrice,
    });
    const collateralUsd = position.getCollateralUsd(payTokenPrice);
    const nextCollateralUsd = collateralUsd.plus(input.value.usd ?? 0);
    const nextLeverage = getLeverage({
      collateralUsd: nextCollateralUsd,
      sizeUsd: position.size.times(position.entryPrice),
    });

    const pairInfos = useTradePairInfos();
    const pairInfo = useMemo(() => {
      return pairInfos?.find(
        (v) => v.address.toLowerCase() === position.pair.address.toLowerCase(),
      );
    }, [pairInfos, position.pair.address]);

    const maxInput = useMaxInput({
      pairInfos: pairInfo,
      leverage: currentLeverage.toNumber(),
      payTokenPrice,
      spreadPercentage: 0, // no spread for deposit collateral
    });

    const minLeverage = useMinLeverage({ position });

    const collateralAmountValidation = useMemo(() => {
      return validateDepositCollateralAmount({
        isLong: position.isLong,
        maxInput,
        balance: maxValue,
        input: input.value,
        minLeverage: minLeverage,
        nextLeverage: nextLeverage,
        isDegen: position.pair.isDegenPair,
      });
    }, [
      position.isLong,
      position.pair.isDegenPair,
      maxInput,
      maxValue,
      input.value,
      minLeverage,
      nextLeverage,
    ]);

    return collateralAmountValidation;
  },
}));
